import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import qs from 'qs';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import { api } from '~/api';
import { useStoreActions, useStoreState } from '~/store';
import { ParticipantData } from '~/store/models/modelTypes';
import { ACL, allowRolesIf, allowRoles } from 'Components/Authorised';
import { apiError } from '~/errors';

export interface ParticipantFilters {
  name: string;
  companyno: string;
  theiraccept: number[];
  ouraccept: number[];
  dismissed?: string;
  viewonly?: string;
  activatedon?: string;
}

const defaultFilters: ParticipantFilters = {
  name: '',
  companyno: '',
  theiraccept: [0, 1],
  ouraccept: [0, 1],
  dismissed: undefined,
  viewonly: undefined,
  activatedon: undefined,
};

const stringFilter = (f: string) => {
  if (!f) return undefined;
  return (
    '%' +
    f
      .trim()
      .replace(/%/g, '')
      .replace(/ +(?= )/g, '')
      .replace(/ /g, '%') +
    '%'
  );
};

const arrayFilter = (value: number[], full: readonly number[]) => {
  if (!value?.length || value.length === full.length) return [];
  return value;
};

const serializeFilters = (f: ParticipantFilters) => {
  const out: Record<string, unknown> = {
    theiraccept: arrayFilter(f.theiraccept, [0, 1]),
    ouraccept: arrayFilter(f.ouraccept, [0, 1]),
  };
  const name = stringFilter(f.name);
  if (name) out.name = name;
  const companyno = stringFilter(f.companyno);
  if (companyno) out.companyno = companyno;
  if (f.dismissed !== undefined) out.dismissed = f.dismissed;
  if (f.viewonly !== undefined) out.viewonly = f.viewonly;
  if (f.activatedon !== undefined) out.activatedon = f.activatedon;
  return out;
};

const deserializeFilters = (
  params: Record<string, string[]>
): ParticipantFilters => {
  const has = (k: string) => Object.prototype.hasOwnProperty.call(params, k);
  const parseStr = (s?: string) => (s ? s.replaceAll('%', ' ').trim() : '');
  return {
    ...defaultFilters,
    name: has('name') ? parseStr(params.name?.[0]) : '',
    companyno: has('companyno') ? parseStr(params.companyno?.[0]) : '',
    theiraccept: has('theiraccept')
      ? [parseInt(params.theiraccept[0], 10)]
      : [0, 1],
    ouraccept: has('ouraccept') ? [parseInt(params.ouraccept[0], 10)] : [0, 1],
    dismissed: has('dismissed') ? params.dismissed[0] : undefined,
    viewonly: has('viewonly') ? params.viewonly[0] : undefined,
    activatedon: has('activatedon') ? params.activatedon[0] : undefined,
  };
};

export interface ParticipantView {
  id: string;
  name: string;
  website: string;
  companyno: string;
  links: ParticipantData['links'];
  created: Dayjs;
  address: ParticipantData['address'] & { description: string };
  viewOnly: boolean;
  buyData: ParticipantData['buydata'];
  sellData: ParticipantData['selldata'];
  tradeRelation: string[];
  us: Omit<ParticipantData['us'], 'updated' | 'requested'> & {
    updated: Dayjs | null;
    requested: Dayjs | null;
  };
  them: Omit<ParticipantData['them'], 'requested'> & {
    requested: Dayjs | null;
  };
  raw: ParticipantData;
  _acl: ACL;
}

const addressDescription = (a: ParticipantData['address']) =>
  [a.street, a.suburb, a.city, a.state, a.country?.name, a.zip]
    .filter(Boolean)
    .join(', ');

export const toParticipantView = (raw: ParticipantData): ParticipantView => {
  const canEdit = () => !raw.viewonly;
  return {
    id: raw.id,
    name: raw.name,
    website: raw.website,
    companyno: raw.companyno,
    links: raw.links,
    created: dayjs(raw.created),
    address: { ...raw.address, description: addressDescription(raw.address) },
    viewOnly: raw.viewonly,
    buyData: raw.buydata,
    sellData: raw.selldata,
    tradeRelation: raw.traderelation,
    us: {
      ...raw.us,
      updated: raw.us.updated ? dayjs(raw.us.updated) : null,
      requested: raw.us.requested ? dayjs(raw.us.requested) : null,
    },
    them: {
      ...raw.them,
      requested: raw.them.requested ? dayjs(raw.them.requested) : null,
    },
    raw,
    _acl: {
      view: allowRoles('guest', 'trader', 'manager'),
      add: allowRolesIf('trader', 'manager')(canEdit),
      edit: allowRolesIf('trader', 'manager')(canEdit),
      delete: allowRolesIf('trader', 'manager')(canEdit),
    },
  };
};

export function useParticipantsAcl(): { _acl: ACL } {
  const solutionSettings = useStoreState((s) => s.auth.solutionSettings);
  const hasParticipants = useCallback(
    () => solutionSettings?.matchinglevel === 'commodity',
    [solutionSettings?.matchinglevel]
  );
  const _acl: ACL = useMemo(
    () => ({
      view: allowRolesIf('guest', 'trader', 'manager')(hasParticipants),
      add: allowRolesIf('trader', 'manager')(hasParticipants),
      edit: allowRolesIf('trader', 'manager')(hasParticipants),
      delete: allowRolesIf('trader', 'manager')(hasParticipants),
    }),
    [hasParticipants]
  );
  return { _acl };
}

interface Pagination {
  page: number;
  limit: number;
}

export function useParticipantsFiltersUrl() {
  const { search } = useLocation();
  const { push } = useHistory();
  const [filters, setFilters] = useState<ParticipantFilters>(defaultFilters);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
  });
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const params = new URLSearchParams(search);
    const fromParams: Record<string, string[]> = {};
    const knownKeys = Object.keys(defaultFilters);
    for (const key of Array.from(params.keys())) {
      if (knownKeys.includes(key)) {
        fromParams[key] = params.getAll(key);
      }
    }
    if (Object.keys(fromParams).length) {
      setFilters(deserializeFilters(fromParams));
    }
  }, [search]);

  const updateFilter = useCallback(
    (name: string, value: unknown) => {
      setFilters((prev) => {
        const next = { ...prev, [name]: value } as ParticipantFilters;
        const serialized = serializeFilters(next);
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(serialized)) {
          if (Array.isArray(v)) {
            for (const item of v) params.append(k, String(item));
          } else if (v !== undefined && v !== '') {
            params.append(k, String(v));
          }
        }
        push({ search: params.toString() });
        return next;
      });
      setPagination((p) => ({ ...p, page: 1 }));
    },
    [push]
  );

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
    push({ search: '' });
    setPagination((p) => ({ ...p, page: 1 }));
  }, [push]);

  const isFiltered =
    JSON.stringify(serializeFilters(defaultFilters)) !==
    JSON.stringify(serializeFilters(filters));

  const filterApi = useMemo(
    () => ({
      filter: updateFilter,
      value: (name: string) =>
        (filters as unknown as Record<string, unknown>)[name],
      values: () => serializeFilters(filters),
      reset: resetFilters,
      filtered: () => isFiltered,
    }),
    [filters, updateFilter, resetFilters, isFiltered]
  );

  return { filters, pagination, setPagination, filterApi };
}

export function useParticipantsList(
  filters: ParticipantFilters,
  pagination: Pagination
) {
  const solution = useStoreState((s) => s.auth.solution);

  const query = useQuery({
    queryKey: [
      'participants',
      solution?.id,
      filters,
      pagination.page,
      pagination.limit,
    ],
    queryFn: async () => {
      const response = await api.request({
        method: 'get',
        url: '/participants',
        params: {
          solution: solution.id,
          page: pagination.page,
          limit: pagination.limit,
          ...serializeFilters(filters),
        },
        paramsSerializer: (p) => qs.stringify(p, { indices: false }),
      });
      const data = response.data ?? {};
      const rawList: ParticipantData[] = data.participants ?? [];
      return {
        items: rawList.map(toParticipantView),
        total: data.total ?? rawList.length,
        pages: data.pages ?? 1,
      };
    },
    enabled: !!solution?.id,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });

  return query;
}

const handleMutationError = (error: unknown) => {
  apiError(error, {});
};

export function useToggleParticipantAcceptance() {
  const queryClient = useQueryClient();
  const solution = useStoreState((s) => s.auth.solution);
  const { formatMessage } = useIntl();

  return useMutation({
    mutationFn: async (participant: ParticipantView) => {
      const docsapproved = !participant.us.docsapproved;
      const response = await api.request({
        method: 'put',
        url: `/participants/${participant.id}`,
        data: { solution: solution.id, accept: docsapproved },
      });
      return response.data as ParticipantData;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
      toast.success(
        formatMessage(
          {
            id: 'participants-toast-acceptance',
            defaultMessage:
              'Successfully {action, select, added {added} other {removed}} {name} as a participant.',
          },
          {
            action: data.us.docsapproved ? 'added' : 'removed',
            name: data.name,
          }
        )
      );
    },
    onError: handleMutationError,
  });
}

export function useChangeParticipantPaymentTerm() {
  const queryClient = useQueryClient();
  const solution = useStoreState((s) => s.auth.solution);
  const { formatMessage } = useIntl();

  return useMutation({
    mutationFn: async (vars: {
      participant: ParticipantView;
      paymentterm: string;
    }) => {
      const response = await api.request({
        method: 'put',
        url: `/participants/${vars.participant.id}`,
        data: {
          solution: solution.id,
          accept: vars.participant.us.docsapproved,
          paymentterm: vars.paymentterm,
        },
      });
      return response.data as ParticipantData;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
      toast.success(
        formatMessage(
          {
            id: 'participants-toast-payment-term',
            defaultMessage:
              'Successfully set {term} as a payment term for {name}.',
          },
          {
            term: data.selldata?.paymentterm?.desc ?? '',
            name: data.name,
          }
        )
      );
    },
    onError: handleMutationError,
  });
}

export function useSendParticipantTradeRequest() {
  const queryClient = useQueryClient();
  const solution = useStoreState((s) => s.auth.solution);
  const { formatMessage } = useIntl();

  return useMutation({
    mutationFn: async (participant: ParticipantView) => {
      const response = await api.request({
        method: 'patch',
        url: `/participants/${participant.id}`,
        data: { solution: solution.id, action: 'request' },
      });
      return { data: response.data as ParticipantData, participant };
    },
    onSuccess: ({ participant }) => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
      toast.success(
        formatMessage(
          {
            id: 'participants-toast-trade-request',
            defaultMessage: 'Successfully sent trade request to {name}.',
          },
          { name: participant.name }
        )
      );
    },
    onError: handleMutationError,
  });
}

export function useDismissParticipants() {
  const queryClient = useQueryClient();
  const solution = useStoreState((s) => s.auth.solution);
  const { formatMessage } = useIntl();
  const dismissParticipants = useStoreActions(
    (a) => a.auth.dismissParticipants
  );

  return useMutation({
    mutationFn: async () => {
      const response = await api.request({
        method: 'patch',
        url: '/participants',
        data: { solution: solution.id, action: 'dismiss' },
      });
      return response.status;
    },
    onSuccess: (status) => {
      if (status === 204) {
        queryClient.invalidateQueries({ queryKey: ['participants'] });
        dismissParticipants();
        toast.success(
          formatMessage({
            id: 'participants-toast-dismissed',
            defaultMessage: 'Participants dismissed successfully',
          })
        );
      }
    },
    onError: handleMutationError,
  });
}
